/**
 * Consent domain for the instrumentation stack. Pure data — this module (and
 * everything under `instrumentation/consent/` except `sources/`) must never
 * import or name a vendor: vendors are subscribers on the bus, and the engine
 * only ever answers "what did the visitor decide, and how do we know".
 */

/**
 * The decidable scopes. Deliberately the same four names as the legacy
 * `ConsentState` (`@/core/consent`) so decisions translate 1:1 across the
 * migration, but defined here rather than imported: the engine must stay
 * standalone so retiring the legacy module later is a delete, not an untangle.
 *
 * `necessary` is intentionally NOT a scope. It cannot be declined, so storing
 * it as a boolean would only create an impossible `false` state; it exists
 * purely as a selector answer (see `hasConsent`).
 */
export type ConsentScope = "functional" | "analytics" | "advertising" | "dataSharing";

export const CONSENT_SCOPES: readonly ConsentScope[] = [
  "functional",
  "analytics",
  "advertising",
  "dataSharing",
] as const;

/**
 * A scope something can REQUIRE: the decidable scopes plus `necessary`, which
 * is not stored state at all — it is always granted by definition, and exists
 * only so strictly-necessary infrastructure can declare itself without
 * pretending to be gated on a decision the visitor never gets to make.
 */
export type RequirableScope = ConsentScope | "necessary";

/** A complete answer: every decidable scope explicitly granted or denied. */
export type ConsentDecision = Readonly<Record<ConsentScope, boolean>>;

/**
 * The decision as the app's consent context has always exposed it. Same shape
 * as `ConsentDecision`; the older name survives for its consumers.
 */
export type ConsentState = ConsentDecision;

/**
 * Tri-state consent for wire contracts that must distinguish "declined" from
 * "never answered": true/false is an explicit decision (an answer, or GPC),
 * null means the visitor has not interacted with the banner yet.
 */
export type ConsentChoice = boolean | null;

/**
 * How the visitor's explicit ANSWER came to be — evidence, not mechanics:
 * - `banner`: the visitor's first explicit answer on this browser.
 * - `override`: a later, different explicit answer (the banner never shows
 *   twice, so it can only have come from reopened preferences).
 *
 * Restoring from storage does not change provenance: a restored banner answer
 * is still a banner answer, so there is no "stored" provenance — a decision
 * with no claim about its channel is not evidence of anything.
 */
export type ConsentAnswerProvenance = "banner" | "override";

/**
 * What a resolved state rests on: the visitor's answer, or `policyDefault` —
 * before they answer, the default of the banner policy they were shown (an
 * opt-out banner grants until the visitor declines). A default is not the
 * visitor's act: it is never persisted, and their first answer after it is a
 * `banner` answer even when the choices match.
 */
export type ConsentBasisProvenance = ConsentAnswerProvenance | "policyDefault";

/**
 * How the EFFECTIVE decision came to be: the basis's own provenance, or
 * `gpc` when Global Privacy Control clamped the governed scopes on top of it.
 * `gpc` is kept distinct from an identical hand-clicked decline because they
 * are different decisions with different legal weight.
 */
export type ConsentProvenance = ConsentBasisProvenance | "gpc";

/**
 * The consent-banner policy the visitor was shown when they answered: the
 * banner provider, that provider's id for the policy, and its mode in the
 * provider's own words (HubSpot: `OPT_IN`, `NO_COOKIES`, …). Evidence of which
 * rules the answer was given under — only the banner knows which policy it
 * matched, because the provider picks it from its own geolocation.
 */
export const BANNER_POLICY_PROVIDERS = ["hubspot"] as const;

export type BannerPolicy = {
  readonly provider: (typeof BANNER_POLICY_PROVIDERS)[number];
  readonly id: string;
  readonly mode: string;
};

type ConsentStatusBase = {
  /**
   * The consent-notice generation this state was built under. Carried in the
   * state (not read from the environment) so the engine stays pure and a
   * stored decision from an older notice can be rejected structurally.
   */
  readonly noticeVersion: string;
  /**
   * Whether Global Privacy Control has been asserted this session. Retained
   * even while unresolved: GPC alone says nothing about `functional`, so it
   * cannot resolve consent by itself, but it must clamp whatever answer
   * arrives later.
   */
  readonly gpcAsserted: boolean;
};

export type UnresolvedConsentStatus = ConsentStatusBase & {
  readonly status: "unresolved";
};

/**
 * A resolved state carries TWO decisions, and the split is load-bearing:
 *
 * - `answer` / `answerProvenance`: what the visitor explicitly decided,
 *   exactly as given (or restored) — never clamped. This is the ONLY thing
 *   that gets persisted. GPC is a browser setting the visitor can switch off
 *   again; persisting a GPC-clamped decision in its place would keep them
 *   denied on the next visit until they re-answered a banner that never
 *   shows twice.
 *
 * - `decision` / `provenance`: what is in EFFECT this session —
 *   `gpcAsserted ? clamp(answer) : answer`, with provenance `gpc` when
 *   clamped and the answer's own otherwise. Everything downstream (selectors,
 *   bus activation, sinks, the recording fingerprint) reads these: the
 *   effective state is what governs vendors and what gets recorded, so a
 *   GPC-forced decline is still recorded as source Gpc.
 */
export type ResolvedConsentStatus = ConsentStatusBase & {
  readonly status: "resolved";
  /** The visitor's answer, or the policy's default when `answerProvenance` is `policyDefault`. */
  readonly answer: ConsentDecision;
  readonly answerProvenance: ConsentBasisProvenance;
  /**
   * The banner policy the ANSWER was given under, or `null` when the banner
   * reported none (c15t, a HubSpot script that never matched a policy, or an
   * answer stored before policies were kept). It belongs to the answer, not the
   * session: it is persisted with it and restored with it, so a later page
   * showing a different policy never relabels an earlier answer.
   */
  readonly answerBannerPolicy: BannerPolicy | null;
  readonly decision: ConsentDecision;
  readonly provenance: ConsentProvenance;
};

/**
 * The engine's whole answer. A sum type rather than booleans-plus-flags so
 * "no decision yet" and "declined everything" cannot be conflated — acting on
 * a decline (deleting identity, tearing a vendor down) while actually
 * unresolved destroys real user state.
 */
export type ConsentStatus = UnresolvedConsentStatus | ResolvedConsentStatus;
